/** WinOverlay — ステージクリア時の祝福モーダル(Phase A2 Day 6)。
 *  Reference: docs/CLAUDE_DESIGN_BRIEF.md §4.6 EoR Success の簡易実装
 *  - 額縁付き(--gallery-frame-gold)、--canvas-cream 地
 *  - "Stage Complete!" + 占領率表示 + Next Stage ボタン
 *  - Day 8 で本格 EoR 画面に置換予定
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { GridConfig } from '../core/types';

export interface WinOverlayOptions {
  readonly grid: GridConfig;
  readonly onContinue: () => void;
}

const COLOR = {
  backdrop: 0x2c2419,
  modalBg: 0xf8f1e4,
  frameGold: 0xc8a85c,
  inkBlack: 0x2c2419,
  inkWarmGrey: 0x6b5d4f,
  museFlora: 0x4a7c3f,
  buttonText: 0xf8f1e4,
} as const;

export class WinOverlay {
  public readonly view: Container;

  private readonly subtitle: Text;
  private readonly rewardText: Text;

  constructor(options: WinOverlayOptions) {
    const { grid, onContinue } = options;

    this.view = new Container();
    this.view.visible = false;
    this.view.eventMode = 'static';

    const backdrop = new Graphics();
    backdrop.rect(0, 0, grid.canvasPx, grid.canvasPx);
    backdrop.fill({ color: COLOR.backdrop, alpha: 0.55 });
    this.view.addChild(backdrop);

    const modalWidth = grid.canvasPx * 0.78;
    const modalHeight = grid.canvasPx * 0.48;
    const modalX = (grid.canvasPx - modalWidth) / 2;
    const modalY = (grid.canvasPx - modalHeight) / 2;

    const modalBg = new Graphics();
    modalBg.roundRect(modalX, modalY, modalWidth, modalHeight, 16);
    modalBg.fill({ color: COLOR.modalBg });
    modalBg.stroke({ width: 2, color: COLOR.frameGold });
    this.view.addChild(modalBg);

    const titleStyle = new TextStyle({
      fontFamily: 'Cormorant Garamond, Georgia, serif',
      fontSize: 36,
      fill: COLOR.inkBlack,
      fontWeight: 'bold',
    });
    const title = new Text({ text: 'Stage Complete!', style: titleStyle });
    title.anchor.set(0.5, 0);
    title.x = grid.canvasPx / 2;
    title.y = modalY + 28;
    this.view.addChild(title);

    const subtitleStyle = new TextStyle({
      fontFamily: 'Inter, "Helvetica Neue", sans-serif',
      fontSize: 16,
      fill: COLOR.inkWarmGrey,
    });
    this.subtitle = new Text({ text: '', style: subtitleStyle });
    this.subtitle.anchor.set(0.5, 0);
    this.subtitle.x = grid.canvasPx / 2;
    this.subtitle.y = modalY + 84;
    this.view.addChild(this.subtitle);

    // 報酬テキスト("+50 Color" など、show() の colorReward で動的に設定)
    const rewardStyle = new TextStyle({
      fontFamily: 'Inter, "Helvetica Neue", sans-serif',
      fontSize: 22,
      fill: 0xff8a00,
      fontWeight: '900',
    });
    this.rewardText = new Text({ text: '', style: rewardStyle });
    this.rewardText.anchor.set(0.5, 0);
    this.rewardText.x = grid.canvasPx / 2;
    this.rewardText.y = modalY + 120;
    this.view.addChild(this.rewardText);

    const decoration = new Graphics();
    for (let i = 0; i < 5; i++) {
      decoration.circle(modalX + 32 + i * 18, modalY + modalHeight - 96, 3);
      decoration.circle(modalX + modalWidth - 32 - i * 18, modalY + modalHeight - 96, 3);
    }
    decoration.fill({ color: COLOR.frameGold });
    this.view.addChild(decoration);

    const buttonW = 200;
    const buttonH = 48;
    const buttonX = (grid.canvasPx - buttonW) / 2;
    const buttonY = modalY + modalHeight - 72;

    const button = new Graphics();
    button.roundRect(buttonX, buttonY, buttonW, buttonH, 24);
    button.fill({ color: COLOR.museFlora });
    button.eventMode = 'static';
    button.cursor = 'pointer';
    button.on('pointertap', () => {
      this.hide();
      onContinue();
    });
    this.view.addChild(button);

    const buttonStyle = new TextStyle({
      fontFamily: 'Inter, "Helvetica Neue", sans-serif',
      fontSize: 16,
      fill: COLOR.buttonText,
      fontWeight: '600',
    });
    const buttonLabel = new Text({ text: 'Next Stage', style: buttonStyle });
    buttonLabel.anchor.set(0.5, 0.5);
    buttonLabel.x = buttonX + buttonW / 2;
    buttonLabel.y = buttonY + buttonH / 2;
    buttonLabel.eventMode = 'none';
    this.view.addChild(buttonLabel);
  }

  show(coverage: number, target: number, colorReward?: number): void {
    this.subtitle.text = `Coverage ${(coverage * 100).toFixed(1)}% / ${(target * 100).toFixed(0)}%`;
    if (colorReward !== undefined && colorReward > 0) {
      this.rewardText.text = `+${colorReward} Color`;
      this.rewardText.visible = true;
    } else {
      this.rewardText.text = '';
      this.rewardText.visible = false;
    }
    this.view.visible = true;
  }

  hide(): void {
    this.view.visible = false;
  }

  isVisible(): boolean {
    return this.view.visible;
  }
}
